// @ts-check
/** @typedef {import('./terrain.js').Palette} Palette */
/** Precision-cut housing with a beveled metal edge.
 * @param {CanvasRenderingContext2D} ctx @param {number} x @param {number} y @param {number} w @param {number} h @param {number} cut
 */
export function housing(ctx, x, y, w, h, cut = 3) {
  ctx.beginPath(); ctx.moveTo(x + cut, y); ctx.lineTo(x + w - cut, y); ctx.lineTo(x + w, y + cut);
  ctx.lineTo(x + w, y + h - cut); ctx.lineTo(x + w - cut, y + h); ctx.lineTo(x + cut, y + h);
  ctx.lineTo(x, y + h - cut); ctx.lineTo(x, y + cut); ctx.closePath(); ctx.fill(); ctx.stroke();
}
/** Machined security units: a tracked scanner and a heavier armored sentinel.
 * @param {CanvasRenderingContext2D} ctx @param {import('../game/types.js').Enemy} enemy @param {number} time @param {Palette} c @param {boolean} reduced
 */
export function paintEnemy(ctx, enemy, time, c, reduced) {
  const core = enemy.mode === 'shell' || enemy.mode === 'roll';
  const heavy = enemy.kind === 'sentinel';
  const tall = enemy.kind === 'monolith';
  const surge = enemy.kind === 'surge', warden = enemy.kind === 'warden';
  const accent = warden ? c.gold : surge ? c.cyan : tall ? c.gold : heavy ? c.violet : c.danger;
  const h = enemy.height;
  ctx.save(); ctx.translate(enemy.x + enemy.width / 2, -enemy.y);
  if (enemy.mode === 'dead') {
    ctx.globalAlpha = Math.min(1, enemy.dead / 0.24);
    if (enemy.launched) ctx.rotate(reduced ? 0 : (0.6 - enemy.dead) * 8);
    else ctx.scale(1.1, 0.3);
  }
  ctx.lineWidth = 0.8;
  const metal = ctx.createLinearGradient(-12, -h, 12, 0);
  metal.addColorStop(0, c.steel); metal.addColorStop(0.2, c.metal); metal.addColorStop(0.6, c.panel); metal.addColorStop(1, c.void);
  ctx.fillStyle = c.void; ctx.strokeStyle = c.edge;
  housing(ctx, -12, -7, 24, 7, 3);
  // Recessed wheel hubs and moving tread pads anchor both units to their collision surface.
  for (const x of [-8, 0, 8]) {
    ctx.fillStyle = c.metal; ctx.beginPath(); ctx.arc(x, -3.5, 2.2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = c.void; ctx.fillRect(x - 0.6, -4.2, 1.2, 1.4);
  }
  const tread = reduced || enemy.vx === 0 ? 0 : (enemy.x * 0.7 % 4 + 4) % 4;
  ctx.fillStyle = c.steel;
  for (let x = -10; x < 10; x += 4) ctx.fillRect(x + tread, -1.1, 1.8, 0.6);
  ctx.fillStyle = metal; ctx.strokeStyle = c.steel;
  housing(ctx, -12, -h + 1, 24, h - 6, core ? 5 : 3);
  if (core) {
    ctx.save(); ctx.translate(0, -h / 2 - 1);
    if (!reduced && enemy.mode === 'roll') ctx.rotate(time * 18);
    ctx.fillStyle = c.void; ctx.strokeStyle = accent; housing(ctx, -6, -6, 12, 12, 3);
    ctx.fillStyle = accent; ctx.fillRect(-2, -2, 4, 4);
    ctx.restore();
  } else {
    // Single glass sensor bar, with a direction-aware scanning lens instead of a drawn face.
    ctx.fillStyle = c.void; ctx.strokeStyle = c.edge; housing(ctx, -9, -h + 6, 18, 8, 1.5);
    const lens = Math.sign(enemy.vx || -1) * 3 + (reduced ? 0 : Math.sin(time * 3) * 1.2);
    ctx.shadowColor = accent; ctx.shadowBlur = reduced ? 0 : 5;
    ctx.fillStyle = accent; ctx.fillRect(lens - 4, -h + 8.5, 8, 2.5); ctx.shadowBlur = 0;
    ctx.fillStyle = c.light; ctx.globalAlpha *= 0.8; ctx.fillRect(lens - 2.5, -h + 8.5, 3, 0.7);
    ctx.globalAlpha = enemy.mode === 'dead' ? Math.min(1, enemy.dead / 0.24) : 1;
    ctx.fillStyle = c.void;
    for (let x = -5; x <= 5; x += 2.5) ctx.fillRect(x, -10, 1, 3);
    ctx.fillStyle = accent; ctx.fillRect(-8, -6.5, 3, 1); ctx.fillRect(5, -6.5, 3, 1);
    if (tall) {
      // Stacked armored cells and a bright crown make its two-sentinel height readable.
      for (let n = 0; n < 3; n++) {
        ctx.fillStyle = c.void; ctx.strokeStyle = c.steel; housing(ctx, -8, -h + 21 + n * 12, 16, 9, 2);
        ctx.fillStyle = accent; ctx.fillRect(-4, -h + 24 + n * 12, 8, 2);
      }
      ctx.fillStyle = accent; ctx.fillRect(-10, -h, 20, 3);
      ctx.fillStyle = c.light; ctx.fillRect(-5, -h, 10, 1);
    }
    if (heavy) {
      ctx.fillStyle = c.metal; ctx.strokeStyle = accent;
      housing(ctx, -12, -h + 1, 5, 5, 1); housing(ctx, 7, -h + 1, 5, 5, 1);
      ctx.fillStyle = accent; ctx.fillRect(-4, -h + 2, 8, 1);
    }
  }
  if ((surge || warden) && !core) {
    ctx.strokeStyle = accent; ctx.lineWidth = 2;
    if (surge) {
      // A forward wedge and charge rails distinguish the fast ground attacker.
      const direction = enemy.attackDirection ?? Math.sign(enemy.vx || -1);
      ctx.beginPath(); ctx.moveTo(-direction * 8, -18); ctx.lineTo(direction * 8, -13); ctx.lineTo(-direction * 8, -8); ctx.stroke();
      for (const x of [-11, 9]) { ctx.fillStyle = accent; ctx.fillRect(x, -h + 2, 2, 12); }
    } else {
      ctx.fillStyle = c.metal; housing(ctx, -10, -h + 18, 20, 14, 4);
      ctx.fillStyle = accent; ctx.fillRect(-7, -h + 23, 5, 4);
      ctx.fillStyle = (enemy.health ?? 2) > 1 ? accent : c.void; ctx.fillRect(2, -h + 23, 5, 4);
      ctx.strokeStyle = c.steel;
      for (const x of [-9, 9]) { ctx.beginPath(); ctx.moveTo(x, -12); ctx.lineTo(x + (x < 0 ? -3 : 3), -7); ctx.lineTo(x, -2); ctx.stroke(); }
    }
    if (enemy.attack === 'tell') {
      // The static symbol remains readable with reduced motion.
      ctx.strokeStyle = accent; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(0, -h - 8, 5, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = c.light; ctx.fillRect(-1, -h - 11, 2, 6);
    }
  }
  ctx.fillStyle = c.steel;
  for (const x of [-9, 9]) { ctx.fillRect(x - 0.5, -h + 3, 1, 1); ctx.fillRect(x - 0.5, -8, 1, 1); }
  ctx.restore();
}
/** Solid architecture uses structural armor; breakable caches show separate scored panels.
 * @param {CanvasRenderingContext2D} ctx @param {import('../game/types.js').Solid} solid @param {number} y @param {Palette} c
 */
export function paintBlock(ctx, solid, y, c) {
  const { x, width: w, height: h } = solid;
  const node = solid.kind === 'node', locked = solid.kind === 'compute' || solid.used;
  const accent = node && !solid.used ? c.violet : locked ? c.steel : c.cyan;
  const material = ctx.createLinearGradient(x, y, x + w, y + h);
  material.addColorStop(0, locked ? c.steel : c.metal); material.addColorStop(0.12, c.panel); material.addColorStop(1, c.void);
  ctx.fillStyle = material; ctx.strokeStyle = accent; ctx.lineWidth = locked ? 1.5 : 1;
  // The collision rectangle stays visibly complete even though its face is inset.
  ctx.fillRect(x, y, w, h); ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  if (locked) {
    ctx.fillStyle = c.metal; ctx.strokeStyle = c.steel; housing(ctx, x + 4, y + 4, w - 8, h - 8, 4);
    ctx.strokeStyle = c.edge; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x + 7, y + 7); ctx.lineTo(x + w - 7, y + h - 7);
    ctx.moveTo(x + w - 7, y + 7); ctx.lineTo(x + 7, y + h - 7); ctx.stroke();
    ctx.fillStyle = c.light; ctx.globalAlpha = 0.6;
    for (const dx of [3, w - 5]) for (const dy of [3, h - 5]) ctx.fillRect(x + dx, y + dy, 2, 2);
    ctx.globalAlpha = 1;
    if (solid.used) { ctx.fillStyle = c.panel; ctx.fillRect(x + 10, y + 10, 12, 12); ctx.strokeStyle = c.steel; ctx.lineWidth = 1.3; ctx.beginPath(); ctx.moveTo(x + 12, y + 16); ctx.lineTo(x + 15, y + 19); ctx.lineTo(x + 21, y + 12); ctx.stroke(); }
  } else if (node) {
    ctx.fillStyle = c.violet; ctx.globalAlpha = 0.18; ctx.fillRect(x + 3, y + 3, w - 6, h - 6); ctx.globalAlpha = 1;
    ctx.strokeStyle = c.violet; ctx.strokeRect(x + 4, y + 4, w - 8, h - 8);
    ctx.fillStyle = c.panel; ctx.strokeStyle = c.light; housing(ctx, x + 9, y + 9, 14, 14, 2);
    ctx.fillStyle = c.violet; ctx.fillRect(x + 15, y + 11, 2, 10); ctx.fillRect(x + 11, y + 15, 10, 2);
    for (let i = 0; i < 3; i++) { ctx.fillRect(x + 11 + i * 4, y + 6, 2, 3); ctx.fillRect(x + 11 + i * 4, y + 23, 2, 3); }
  } else {
    for (let row = 0; row < 2; row++) for (let col = 0; col < 2; col++) {
      const px = x + 3 + col * 14, py = y + 3 + row * 14;
      ctx.fillStyle = c.metal; ctx.strokeStyle = c.edge; housing(ctx, px, py, 12, 12, 1);
      ctx.fillStyle = c.cyan; ctx.fillRect(px + 2, py + 2, 5, 1);
    }
    ctx.strokeStyle = c.void; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(x + 15, y + 2); ctx.lineTo(x + 18, y + 10); ctx.lineTo(x + 14, y + 17); ctx.lineTo(x + 17, y + 30); ctx.stroke();
  }
}
/** @param {CanvasRenderingContext2D} ctx @param {import('../game/types.js').Pickup} item @param {Palette} c @param {boolean} reduced */
export function paintPickup(ctx, item, c, reduced) {
  const { x, y, width: w, height: h, kind } = item;
  const accent = kind === 'bit' || kind === 'overclock' ? c.gold : kind === 'pulse' ? c.violet : c.cyan;
  ctx.save(); ctx.translate(x + w / 2, -y - h / 2);
  ctx.shadowColor = accent; ctx.shadowBlur = reduced ? 0 : 8;
  ctx.fillStyle = c.panel; ctx.strokeStyle = accent; ctx.lineWidth = 1.5;
  housing(ctx, -w / 2 + 1, -h / 2, w - 2, h, kind === 'bit' ? 6 : 4); ctx.shadowBlur = 0;
  ctx.strokeStyle = c.light; ctx.lineWidth = 1;
  if (kind === 'bit') { ctx.strokeRect(-3, -4, 6, 8); ctx.fillStyle = accent; ctx.fillRect(-1, -3, 2, 6); }
  else if (kind === 'core') { ctx.fillStyle = accent; ctx.fillRect(-2, -6, 4, 12); ctx.fillRect(-6, -2, 12, 4); }
  else if (kind === 'pulse') { ctx.strokeStyle = accent; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-6, 4); ctx.lineTo(0, -4); ctx.lineTo(0, 4); ctx.lineTo(6, -4); ctx.stroke(); }
  else { ctx.fillStyle = accent; ctx.beginPath(); ctx.moveTo(2, -8); ctx.lineTo(-5, 1); ctx.lineTo(0, 1); ctx.lineTo(-2, 8); ctx.lineTo(6, -2); ctx.lineTo(1, -2); ctx.closePath(); ctx.fill(); }
  ctx.restore();
}

/** A three-lead transistor keeps its gray silhouette after permanent collection.
 * @param {CanvasRenderingContext2D} c @param {number} x @param {number} y
 * @param {boolean} found @param {Palette} p
 */
export function paintTransistor(c, x, y, found, p) {
  c.save(); c.translate(x, -y); c.globalAlpha = found ? .4 : 1;
  const color = found ? p.steel : p.gold;
  c.strokeStyle = color; c.lineWidth = 2;
  for (const pin of [-7, 0, 7]) { c.beginPath(); c.moveTo(pin, 3); c.lineTo(pin, 14); c.stroke(); }
  c.fillStyle = found ? p.panel : p.gold;
  c.beginPath(); c.arc(0, -3, 11, Math.PI, 0); c.lineTo(11, 4); c.lineTo(-11, 4); c.closePath(); c.fill(); c.stroke();
  c.fillStyle = found ? p.steel : p.void; c.font = 'bold 9px monospace'; c.textAlign = 'center'; c.fillText('T', 0, 1);
  c.restore();
}

/** Signs teach a move without words: two rising chevrons mean jump twice; a low
 * double arrow under a gate means go under. Drawn over the block, static for reduced motion.
 * @param {CanvasRenderingContext2D} ctx @param {import('../game/types.js').Solid} solid @param {number} y @param {{cyan:string,void:string}} c
 */
export function paintSign(ctx, solid, y, c) {
  const { x, width: w, height: h } = solid;
  ctx.save();
  ctx.strokeStyle = c.cyan; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  if (solid.sign === 'double-jump') {
    ctx.globalAlpha = .55; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x + 3, y + 6); ctx.lineTo(x + 3, y + h - 6); ctx.moveTo(x + w - 3, y + 6); ctx.lineTo(x + w - 3, y + h - 6); ctx.stroke();
    const size = Math.min(w * .32, 14), mid = x + w / 2;
    ctx.globalAlpha = 1; ctx.lineWidth = 4; ctx.strokeStyle = c.void;
    for (const pass of [0, 1]) {
      if (pass) { ctx.lineWidth = 2.5; ctx.strokeStyle = c.cyan; }
      for (const top of [y + h * .18, y + h * .18 + size * 1.25]) {
        ctx.beginPath(); ctx.moveTo(mid - size, top + size); ctx.lineTo(mid, top); ctx.lineTo(mid + size, top + size); ctx.stroke();
      }
    }
  } else if (solid.sign === 'slide') {
    const base = y + h, size = 7;
    ctx.fillStyle = c.cyan; ctx.globalAlpha = .7; ctx.fillRect(x, base - 3, w, 3); ctx.globalAlpha = 1;
    ctx.lineWidth = 2.5;
    for (const left of [x + w / 2 - size * 1.4, x + w / 2]) {
      const mid = base - 13;
      ctx.beginPath(); ctx.moveTo(left, mid - size); ctx.lineTo(left + size, mid); ctx.lineTo(left, mid + size); ctx.stroke();
    }
  }
  ctx.restore();
}

